import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import Spinner from '$components/Spinner/Spinner';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { singlePositionSlice } from '$store/modules/position/position.reducer';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseModal } from '$icons/uncolored/Close.svg';
import { ReactComponent as ThumbDown } from '$icons/uncolored/thumbs-down.svg';
import { ReactComponent as Interview } from '$icons/uncolored/comments.svg';
import { ReactComponent as Arrow } from '$icons/uncolored/arrow.svg';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import { useCallback, useEffect } from 'react';

import classes from './SPKFullProfileIframeModal.module.css';

const SPKFullProfileIframeModal = () => {
  const dispatch = useAppDispatch();
  const { selectedCandidate, isLoadingIframe, openFullSPKProfile } =
    useAppSelector((state) => state.position);
  const { currency, currencyCode, usdToCurrencyConverter } = useAppSelector(
    (state) => state.brand
  );
  const [t] = useTranslation('global');

  const ratio = usdToCurrencyConverter(1);

  const keyboardArrowListener = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.code === 'ArrowLeft') {
        dispatch(singlePositionSlice.actions.showPreviousCandidate());
      } else if (ev.code === 'ArrowRight') {
        dispatch(singlePositionSlice.actions.showNextCandidate());
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (openFullSPKProfile)
      document.addEventListener('keyup', keyboardArrowListener);
    else document.removeEventListener('keyup', keyboardArrowListener);

    return () => document.removeEventListener('keyup', keyboardArrowListener);
  }, [keyboardArrowListener, openFullSPKProfile]);

  return (
    <ModalInTailwind
      isOpen={openFullSPKProfile}
      closeModal={() =>
        dispatch(singlePositionSlice.actions.setOpenFullSPKProfile(false))
      }
      blurBackground
    >
      <div className="container w-full h-screen p-4">
        <div className="h-full overflow-hidden flex flex-col">
          {/* Actions bar */}
          <div className="bg-[#d6e3ff] mb-4 rounded">
            <div className="pointer-events-auto pt-4 flex justify-between gap-4 p-4 items-center">
              <div className="flex flex-row gap-4 *:bg-spk-neutral-20 *:py-1 *:px-2 *:rounded">
                <button
                  type="button"
                  className="text-left text-nowrap flex flex-row gap-2 items-center"
                  onClick={() => {
                    dispatch(
                      singlePositionSlice.actions.showPreviousCandidate()
                    );
                  }}
                >
                  <Arrow className="w-4 h-4 rotate-90" />
                  {t('modalCandidateActions.prevCandidate')}
                </button>
                <button
                  className="text-left text-nowrap flex flex-row gap-2 items-center"
                  type="button"
                  onClick={() => {
                    dispatch(singlePositionSlice.actions.showNextCandidate());
                  }}
                >
                  {t('modalCandidateActions.nextCandidate')}
                  <Arrow className="w-4 h-4 -rotate-90" />
                </button>
              </div>
              <div className="flex flex-row gap-4 *:bg-spk-neutral-20 *:py-1 *:px-2 *:rounded">
                <button
                  className="text-left text-nowrap flex flex-row gap-2 items-center !bg-employer-primary text-white !px-4"
                  type="button"
                  onClick={() => {
                    dispatch(
                      singlePositionSlice.actions.setOpenFullSPKProfile(false)
                    );
                    dispatch(
                      singlePositionSlice.actions.setOpenConfirmationInterviewModal(
                        true
                      )
                    );
                  }}
                >
                  <Interview className="fill-white w-5 h-5" />
                  {t('modalCandidateActions.buttonInterview')}
                </button>
                <button
                  className="text-left text-nowrap flex flex-row gap-2 items-center text-spk-brightRed"
                  type="button"
                  onClick={() => {
                    dispatch(
                      singlePositionSlice.actions.setOpenFullSPKProfile(false)
                    );
                    dispatch(
                      singlePositionSlice.actions.setOpenNotAFitModal(true)
                    );
                  }}
                >
                  <ThumbDown className="fill-spk-brightRed w-4 h-4" />
                  {t('modalCandidateActions.buttonReject')}
                </button>
                <button
                  type="button"
                  className="flex flex-row col-span-2 gap-2 items-center justify-center"
                  onClick={() => {
                    dispatch(
                      singlePositionSlice.actions.setOpenFullSPKProfile(false)
                    );
                  }}
                >
                  {t('common.close')}
                  <CloseModal className="w-5 h-5 stroke-spk-highContrast" />
                </button>
              </div>
            </div>
          </div>
          {/* Iframe */}
          <div className="p-4 w-full h-full bg-spk-neutral-20 rounded overflow-hidden flex flex-col">
            <div
              className={`${
                !isLoadingIframe && 'hidden'
              } flex items-center justify-center w-full h-full`}
            >
              <Spinner width="100%" height="100%" />
            </div>
            <iframe
              title={`${selectedCandidate?.User?.FirstName} Profile`}
              src={`${import.meta.env.REACT_APP_SPK_URL}/spk/${
                selectedCandidate?.Id
              }?hideNavbar=true&currency=${currencyCode}&ratio=${ratio}&currencySymbol=${encodeURIComponent(
                currency
              )}`}
              className={`w-full h-full pointer-events-auto ${
                isLoadingIframe ? 'max-h-0' : 'max-h-auto'
              } ${classes.zoomedIframe}`}
              onLoad={() =>
                dispatch(singlePositionSlice.actions.setIsLoadingIframe(false))
              }
            />
          </div>
        </div>
      </div>
    </ModalInTailwind>
  );
};

export default SPKFullProfileIframeModal;
